import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Mail, Heart, Phone, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage, translations } from "../context/LanguageContext";
import LanguageToggle from "./LanguageToggle";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [certPageEnabled, setCertPageEnabled] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    axios.get(`${API}/certificates/settings`)
      .then(res => setCertPageEnabled(res.data.enabled))
      .catch(() => {});
  }, []);

  const handleEmergencyExit = () => {
    window.open("https://www.google.com", "_newtab");
    window.location.replace("https://www.google.com");
  };

  const baseNavigation = [
    { name: t.nav.home, href: "/" },
    { name: t.nav.services || "Our Services", href: "/services" },
    { name: t.nav.training || "Professional Training", href: "/training" },
    { name: t.nav.referrals, href: "/referrals" },
    { name: t.nav.about, href: "/about" },
    { name: t.nav.vision || "Our Vision", href: "/vision" },
    { name: t.nav.getInvolved || "Get Involved", href: "/get-involved" },
  ];

  const navigation = certPageEnabled
    ? [...baseNavigation, { name: t.certificate?.navLabel || "Certificate", href: "/certificate" }]
    : baseNavigation;

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-warm-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:outline-none"
      >
        {language === 'es' ? 'Saltar al contenido' : 'Skip to content'}
      </a>

      {/* Top bar */}
      <div className="bg-warm-800 text-white py-2" role="banner">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-xs">
          <div className="flex items-center gap-4">
            <a href="tel:347-221-4189" className="flex items-center gap-1.5 hover:text-warm-200 transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">(347) 221-4189</span>
            </a>
            <span className="hidden sm:inline text-warm-500">|</span>
            <a href="tel:1-800-799-7233" className="flex items-center gap-1.5 font-semibold hover:text-warm-200 transition-colors">
              <span>DV Hotline: 1-800-799-7233</span>
            </a>
            <span className="hidden sm:inline text-warm-500">|</span>
            <a href="mailto:director@thepillarprojectbaltimore.org" className="hidden sm:flex items-center gap-1.5 hover:text-warm-200 transition-colors">
              <Mail className="w-3.5 h-3.5" />
              <span>director@thepillarprojectbaltimore.org</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/portal/login" className="hover:text-warm-200 transition-colors" data-testid="nav-portal-link">
              {language === 'es' ? 'Mi Portal' : 'My Portal'}
            </Link>
            <Link to="/partner/login" className="hover:text-warm-200 transition-colors">
              {language === 'es' ? 'Socios' : 'Partners'}
            </Link>
            <Link to="/admin" className="hover:text-warm-200 transition-colors">
              Admin
            </Link>
            <span className="text-warm-600">|</span>
            <LanguageToggle />
            <button
              onClick={handleEmergencyExit}
              className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded font-bold text-xs transition-colors"
              data-testid="emergency-exit-btn"
              title="Quickly leave this site"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{language === 'es' ? 'OCULTAR SITIO' : 'HIDE WEBSITE'}</span>
              <span className="sm:hidden">EXIT</span>
            </button>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="glass-header sticky top-0 z-50" role="navigation" aria-label={language === 'es' ? 'Navegaci\u00f3n principal' : 'Main navigation'}>
        <nav className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0 mr-4" data-testid="logo-link">
              <img 
                src="/logo-new.png" 
                alt="The Pillar Project" 
                className="h-12 w-auto object-contain"
              />
              <div className="hidden md:block leading-tight">
                <span className="font-semibold text-gray-900 text-base tracking-tight" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  The Pillar Project
                </span>
                <span className="block text-[10px] text-warm-500 font-medium">Corporation</span>
              </div>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? "text-warm-700"
                      : "text-gray-600 hover:text-warm-700"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/donate">
                <Button data-testid="header-donate-btn" variant="outline" className="border-warm-700 text-warm-700 hover:bg-warm-50 rounded-full px-6 gap-2">
                  <Heart className="w-4 h-4" />
                  {t.nav.donate}
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="header-gethelp-btn" className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-6">
                  {t.nav.getSupport || 'Get Support'}
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4">
              <div className="flex flex-col gap-3">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-sm font-medium py-2 ${
                      isActive(item.href)
                        ? "text-warm-700"
                        : "text-gray-600"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link to="/donate" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="border-warm-700 text-warm-700 hover:bg-warm-50 rounded-full w-full mt-2 gap-2">
                    <Heart className="w-4 h-4" />
                    {t.nav.donate}
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="bg-warm-700 hover:bg-warm-800 text-white rounded-full w-full mt-2">
                    {t.nav.getHelp}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main content */}
      <main id="main-content" className="flex-1" role="main" tabIndex="-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-warm-50 border-t border-warm-200" role="contentinfo">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/logo-new.png" 
                  alt="The Pillar Project" 
                  className="h-14 w-auto object-contain rounded-lg"
                />
                <div>
                  <span className="font-semibold text-gray-900 text-lg tracking-tight" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                    The Pillar Project
                  </span>
                  <span className="block text-xs text-warm-500 font-medium">Corporation</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed max-w-md">
                {t.footer.description}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{t.footer.quickLinks}</h4>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm text-gray-600 hover:text-warm-700 transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/resources" className="text-sm text-gray-600 hover:text-warm-700 transition-colors">
                    {t.nav.resources}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-warm-700 transition-colors">
                    {t.nav.contact}
                  </Link>
                </li>
                <li>
                  <Link to="/careers" className="text-sm text-gray-600 hover:text-warm-700 transition-colors" data-testid="footer-careers-link">
                    {language === 'es' ? 'Carreras' : 'Careers'}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{t.footer.contact}</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>
                  <a href="tel:347-221-4189" className="hover:text-warm-700 transition-colors font-medium">
                    (347) 221-4189
                  </a>
                </li>
                <li>
                  <a href="mailto:director@thepillarprojectbaltimore.org" className="hover:text-warm-700 transition-colors break-all">
                    director@thepillarprojectbaltimore.org
                  </a>
                </li>
                <li>Baltimore, Maryland</li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} The Pillar Project Corporation. {t.footer.copyright}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-warm-700 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-warm-700 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
